import { inject, Injectable, Type } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";

import { CommandBarButton } from "../types";
import { UnsubscribeOnDestroy } from "../classes";

export class CommandBar {
    buttons: CommandBarButton[];
    showGroupSeparator?: boolean;
}

@Injectable({
    "providedIn": "root"
})
export class CommandBarService extends UnsubscribeOnDestroy {
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private commandbarMap: { [key: string]: [CommandBar, Subject<any>] } = {};
    private routes: { [key: string]: string } = {};
    private commandbarSubject: Subject<[CommandBar, Subject<any>]> = new Subject<[CommandBar, Subject<any>]>();

    constructor() {
        super();

        this.router.events
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                filter((event) => event instanceof NavigationEnd)
            )
            .subscribe((event: NavigationEnd) => {
                let snapshot = this.route.snapshot.root;
                let child = snapshot.children.find((c) => c.outlet === "primary");
                while (child) {
                    snapshot = child;
                    child = snapshot.children.find((c) => c.outlet === "primary");
                }
                if (snapshot.component as Type<any>) {
                    const component = snapshot.component as Type<any>;
                    if (component.name) {
                        this.commandbarSubject.next(this.commandbarMap[component.name]);
                        return;
                    }
                }
                this.commandbarSubject.next(null);
            });
    }

    get commandBar(): Observable<[CommandBar, Subject<any>]> {
        return this.commandbarSubject;
    }

    addButton(component: any, btn: any) {
        this.commandbarMap[component.constructor.name][0].buttons.push(btn);
        this.commandbarSubject.next(this.commandbarMap[component.constructor.name]);
    }

    removeButton(component: any, btnResult: string) {
        const index = this.commandbarMap[component.constructor.name][0].buttons.findIndex((btn) => btn.result === btnResult);
        this.commandbarMap[component.constructor.name][0].buttons.splice(index, 1);
        this.commandbarSubject.next(this.commandbarMap[component.constructor.name]);
    }

    updateButtonText(component: any, buttonResponse: string, buttonText: string) {
        const cmdButton: CommandBarButton = this.commandbarMap[component.constructor.name][0].buttons.find((btn: CommandBarButton) => btn.result === buttonResponse);
        if (cmdButton !== undefined) {
            cmdButton.text = buttonText;
        }
    }

    updateButtonVisibility(component: any, buttonResponse: string, isHidden: boolean) {
        const cmdButton: CommandBarButton = this.commandbarMap[component.constructor.name][0].buttons.find((btn: CommandBarButton) => btn.result === buttonResponse);
        if (cmdButton !== undefined) {
            cmdButton.hide = isHidden;
        }
    }

    setCommandBar(component: any, commandBar: CommandBar, result?: Observable<any>, componentName: string = null): Observable<any> {
        if (!componentName) {
            componentName = component.constructor.name;
        }

        let subject = new Subject<any>();
        if (result && result instanceof Subject) {
            subject = <Subject<any>>result;
        }

        this.commandbarMap[componentName] = [commandBar, subject];

        let snapshot = this.route.snapshot.root;
        let child = snapshot.children.find((c) => c.outlet === "primary");
        while (child) {
            snapshot = child;
            child = snapshot.children.find((c) => c.outlet === "primary");
        }
        if (snapshot.component as Type<any>) {
            const c = snapshot.component as Type<any>;
            if (c.name === componentName) {
                this.commandbarSubject.next(commandBar ? [commandBar, subject] : null);
            }
        }

        return subject;
    }

    toggleButton(component: any, btnResult: string, enable: boolean, componentName: string = null) {
        if (!componentName) {
            componentName = component.constructor.name;
        }

        let currentButton = this.commandbarMap[componentName][0].buttons.find((btn) => btn.result === btnResult);

        if (currentButton) {
            currentButton.enabled = enable;
        }
    }
}
